import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils';
import { getCurrentLang } from '../i18n/i18n';
import { Check, Pencil, X } from 'lucide-react';
import { formatAtStation } from '../utils/stationTime';
import { formatBookedWindowLabel } from '../utils/nonRoomAvailability';
import { useReservationSummary } from './reservationSummary/useReservationSummary';
import AlreadyPaidSection from './reservationSummary/AlreadyPaidSection';
import TaxBreakdown from './reservationSummary/TaxBreakdown';
import { PriceBreakdown } from '../api/booking/pricePreviewApiTypes';
import PricePreviewStateRow, { PricePreviewStatus } from './reservationSummary/PricePreviewStateRow';

// The money rows this box renders once the backend breakdown arrives. Shown
// greyed out beforehand so the operator sees what is coming instead of a bare
// "n.z." — the values stay "wird berechnet", never a zero (see
// PricePreviewStateRow).
const PENDING_ROW_KEYS = [
    'booking_details.reservation_summary.price',
    'booking_details.reservation_summary.coupon_applied',
    'booking_details.reservation_summary.total',
    'booking_details.reservation_summary.already_paid',
    'booking_details.reservation_summary.amount_due',
];

// A prepaid voucher pays part of the price; it is not a price reduction, so
// it is never labelled "Rabatt" (#3332 D2).
const reductionLabelKey = (isPrepaidVoucher: boolean): string =>
    isPrepaidVoucher
        ? 'booking_details.reservation_summary.prepaid_voucher_applied'
        : 'booking_details.reservation_summary.coupon_applied';

// Storage / SUP price box: discount only, no surcharges.
const MIN_DISCOUNT_AMOUNT = 0;

interface NonRoomRentalReservationSummaryProps {
    startDate: string | null;
    // #3219: the zone of the booked location; the station default when omitted.
    timeZone?: string;
    endDate: string | null;
    locationName?: string;
    groupDescription?: string;
    groupLangNames?: {
        de: { title: string, description: string };
        en: { title: string, description: string };
    };
    discount: number;
    // The reduction is a prepaid voucher paying part of the price, not a price
    // reduction — labelled "Gutschein", never "Rabatt" (#3332 D2).
    discountIsPrepaidVoucher?: boolean;
    // Passing a handler makes the discount inline-editable (edit surface);
    // omitting it renders the discount as a read-only line. Symmetric with
    // `onAlreadyPaidChange` below — the booking-detail view shows the same box
    // without offering edits.
    onDiscountChange?: (newDiscount: number) => void;
    // Amount already paid (offline / provider). When present, the already-paid and
    // amount-due rows render. Passing `onAlreadyPaidChange` turns the row into the
    // inline-editable "mark as paid" control (edit surface); omitting it renders a
    // read-only already-paid line. Mirrors the venue/room boxes — see
    // docs/plans/generalized-reservation-with-prepayment.md.
    amountPaid?: number;
    onAlreadyPaidChange?: (newAmountPaid: number) => void;
    // Backend price breakdown (#1203) — the single source for every money row.
    // While it is absent a state row is shown instead of the money rows; there
    // is no FE-computed fallback.
    breakdown?: PriceBreakdown;
    // Why the breakdown is absent (idle / loading / error) — see PricePreviewStateRow.
    previewStatus?: PricePreviewStatus;
}

const NonRoomRentalReservationSummary: React.FC<NonRoomRentalReservationSummaryProps> = ({
    startDate,
    timeZone,
    endDate,
    discount,
    discountIsPrepaidVoucher = false,
    onDiscountChange,
    amountPaid,
    onAlreadyPaidChange,
    breakdown,
    previewStatus = 'loading',
}) => {
    const { t } = useTranslation();

    const summary = useReservationSummary({
        couponApplied: discount,
        amountPaid,
        minDiscount: MIN_DISCOUNT_AMOUNT,
        onDiscountChange,
        onAlreadyPaidChange,
        breakdown,
    });

    // Pre-discount total (BE subtotal). Only rendered when the breakdown is here.
    const displayPrice = breakdown?.subtotal ?? 0;

    const {
        editingDiscount,
        setEditingDiscount,
        tempDiscount,
        setTempDiscount,
        isDiscountValid,
        confirmDiscount,
        cancelDiscount,
        adjustedTotal,
        net,
        tax,
    } = summary;

    // The same label the booking-details field shows for this window.
    const durationLabel = formatBookedWindowLabel(startDate, endDate, t);

    // Times on the station's clock (ADR-0019), not the operator's browser.
    const getDateTimeFormat = () => {
        const lang = getCurrentLang();
        return lang === 'de' ? 'dd LLL yyyy, HH:mm' : 'dd LLL yyyy, h:mm a';
    };

    return (
        <div className={'w-[90%] col-span-12 md:col-span-4 border border-foreground/20 rounded py-4 px-3 mx-auto xl:ml-auto xl:mr-0 min-h-[32px]'}>
            <h3 className={'text-lg text-center font-semibold mb-3'}>{t('booking_details.reservation_summary.summary')}</h3>
            <div className={'border-b border-gray mb-4'}></div>

            {/* Money rows render the BE breakdown only (#1203); while the debounced
                preview is in flight a placeholder is shown — never an FE number. */}
            {breakdown && durationLabel && (
                <div className={'mb-2 flex justify-between text-sm'}>
                    <strong>{durationLabel}</strong>
                    <span className={'font-semibold'}>{formatCurrency(displayPrice, getCurrentLang())}</span>
                </div>
            )}

            {startDate && (
                <div className={'text-sm mb-2 flex justify-between'}>
                    <strong>{t('create_booking.reservation_details.start_date')}</strong>
                    <span className={'text-gray-600'}>{formatAtStation(startDate, getDateTimeFormat(), 'en', timeZone)}</span>
                </div>
            )}
            {endDate && (
                <div className={'text-sm mb-2 flex justify-between'}>
                    <strong>{t('create_booking.reservation_details.end_date')}</strong>
                    <span className={'text-gray-600'}>{formatAtStation(endDate, getDateTimeFormat(), 'en', timeZone)}</span>
                </div>
            )}

            {!breakdown ? (
                /* No breakdown yet (idle / loading / error) — a state row, never an FE-computed number. */
                <PricePreviewStateRow
                    status={previewStatus}
                    rowLabelKeys={PENDING_ROW_KEYS}
                />
            ) : (
                <>
                    {displayPrice > 0 && (
                        <div className={'text-sm mb-2 flex justify-between'}>
                            <strong>{t('booking_details.reservation_summary.price')}</strong>
                            <span className={'text-gray-600'}>{formatCurrency(displayPrice, getCurrentLang())}</span>
                        </div>
                    )}

                    {/* Discount Section */}
                    <div className={'flex flex-col justify-between text-sm mt-4'}>
                        <div className={'flex items-center gap-2 w-full'}>
                            <strong>{t(reductionLabelKey(discountIsPrepaidVoucher))}</strong>
                            <div className={'ml-auto flex items-center gap-2'}>
                                {editingDiscount ? (
                                    <>
                                        <input
                                            type={'number'}
                                            className={`
                                                    w-20 px-2 py-0.5 border rounded text-right text-sm h-[28px] appearance-none
                                                    [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                                                    focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary
                                                    ${!isDiscountValid ? 'border-red-500 bg-red-50' : 'border-gray-300'}
                                                `}
                                            value={tempDiscount}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^\d*(?:\.\d*)?$/.test(value)) {
                                                    setTempDiscount(value);
                                                }
                                            }}
                                            onFocus={() => {
                                                if (tempDiscount === '0') {
                                                    setTempDiscount('');
                                                }
                                            }}
                                            placeholder={'€'}
                                            autoFocus
                                        />
                                        <span className={'text-xs'}>€</span>
                                        <button
                                            onClick={confirmDiscount}
                                            className={`${
                                                isDiscountValid ? 'text-green-600 hover:text-green-800' : 'text-gray-400 cursor-not-allowed'
                                            } h-[28px] flex items-center`}
                                            disabled={!isDiscountValid}
                                        >
                                            <Check size={18} />
                                        </button>
                                        <button onClick={cancelDiscount} className={'text-red-600 hover:text-red-800 h-[28px] flex items-center'}>
                                            <X size={18} />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className={'text-sm text-right h-[28px] leading-[28px] inline-block'}>
                                            -{formatCurrency(Math.abs(discount), getCurrentLang())}
                                        </div>
                                        {onDiscountChange && (
                                            <button
                                                onClick={() => setEditingDiscount(true)}
                                                className={'text-gray-500 hover:text-black h-[28px] flex items-center'}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {editingDiscount && !isDiscountValid && (
                            <p className={'text-red-500 text-xs mt-2'}>
                                {t('booking_details.reservation_summary.discount_error', {
                                    min: `${formatCurrency(MIN_DISCOUNT_AMOUNT, getCurrentLang())}`,
                                    max: formatCurrency(displayPrice, getCurrentLang()),
                                    defaultValue: 'The discount amount must be between {{min}} and {{max}}.',
                                })}
                            </p>
                        )}
                    </div>

                    <div className={'border-b border-gray my-2'}></div>

                    {/* The shared block, not a copy of it (#1353). This screen
                        used to re-implement the same three rows inline, which is
                        why it kept its own `(taxRate * 100).toFixed(0)` and never
                        got the per-rate table. */}
                    <TaxBreakdown
                        displayTotal={adjustedTotal}
                        taxBreakdown={breakdown?.taxBreakdown}
                        taxExemptionNote={breakdown?.taxExemptionNote}
                        taxUnresolved={breakdown?.taxUnresolved}
                        tax={tax}
                        net={net}
                    />

                    {/* Already-paid + amount-due (shared section). Editable when
                        `onAlreadyPaidChange` is provided (edit surface). Callers
                        that don't wire the mark-as-paid control (e.g. the
                        reschedule view) fall back to the read-only rows from the
                        BE breakdown — only shown once money has actually moved. */}
                    {amountPaid !== undefined ? (
                        <AlreadyPaidSection summary={summary} amountPaid={amountPaid} editable={!!onAlreadyPaidChange} />
                    ) : (
                        breakdown.amountPaid > 0 && (
                            <>
                                <div className={'flex justify-between text-sm mt-2'}>
                                    <span>{t('booking_details.reservation_summary.already_paid')}</span>
                                    <span className={'font-bold'}>{formatCurrency(breakdown.amountPaid, getCurrentLang())}</span>
                                </div>
                                <div className={'flex justify-between text-sm mt-1'}>
                                    <span className={'font-semibold'}>
                                        {breakdown.amountDue > 0
                                            ? t('booking_details.reservation_summary.amount_due')
                                            : t('booking_details.reservation_summary.amount_overpaid')}
                                    </span>
                                    <span className={'font-bold'}>
                                        {formatCurrency(Math.abs(breakdown.amountDue), getCurrentLang())}
                                    </span>
                                </div>
                            </>
                        )
                    )}
                </>
            )}
        </div>
    );
};

export default NonRoomRentalReservationSummary;
